import React, { useState } from 'react';
import { CheckCircle2, ArrowRight, ShieldCheck, Sparkles, Send } from 'lucide-react';
import { Button } from './ui/Button.jsx';
import { Input } from './ui/Input.jsx';
import { Badge } from './ui/Badge.jsx';

export function ConsultationForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'Full-Stack MERN Web App / SaaS',
    budget: '$1,500 - $5,000',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorMsg) setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = formData.email.trim().toLowerCase();

    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setErrorMsg('Please enter a valid business or Gmail address.');
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: cleanEmail,
          phone: formData.phone.trim(),
          service: formData.service,
          budget: formData.budget,
          notes: formData.notes.trim(),
          source: 'homepage-consultation'
        })
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Unable to submit your project brief.');
      }

      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="bza-submit-success">
        <CheckCircle2 size={44} color="var(--success-base)" style={{ margin: '0 auto 8px', display: 'block' }} />
        <h4>Project Brief Received!</h4>
        <p>
          Thank you, <strong>{formData.name || formData.email}</strong>. Our engineering lead will review your requirements and reach out via email within 24 hours.
        </p>
        <button
          type="button"
          onClick={() => {
            setStatus('idle');
            setFormData({
              name: '',
              email: '',
              phone: '',
              service: 'Full-Stack MERN Web App / SaaS',
              budget: '$1,500 - $5,000',
              notes: ''
            });
          }}
          style={{
            marginTop: '16px',
            background: 'transparent',
            border: 'none',
            color: 'var(--success-text)',
            fontSize: '13px',
            fontWeight: 600,
            textDecoration: 'underline',
            cursor: 'pointer'
          }}
        >
          Submit another inquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bza-consultation-form">
      <div className="bza-form-row">
        <Input
          label="Your Full Name"
          name="name"
          placeholder="e.g. Alton Halder"
          value={formData.name}
          onChange={handleChange}
        />

        <Input
          label="Work / Business Email"
          name="email"
          type="email"
          required
          placeholder="you@company.com"
          value={formData.email}
          onChange={handleChange}
        />
      </div>

      <div className="bza-form-row bza-form-row-services">
        <Input
          as="select"
          label="Project Service Category"
          name="service"
          value={formData.service}
          onChange={handleChange}
        >
          <option value="Full-Stack MERN Web App / SaaS">Full-Stack MERN Web App / SaaS</option>
          <option value="Custom Workflow Automation">Custom Workflow Automation</option>
          <option value="High-Converting Landing Page / CMS">Landing Page / CMS</option>
          <option value="Technical SEO & Performance Optimization">SEO & Performance Optimization</option>
          <option value="Custom AI Integration / API Pipeline">AI Integration / API Pipeline</option>
        </Input>

        <Input
          as="select"
          label="Estimated Budget Range"
          name="budget"
          value={formData.budget}
          onChange={handleChange}
        >
          <option value="Under $500">Under $500</option>
          <option value="$500 - $1,500">$500 - $1,500</option>
          <option value="$1,500 - $5,000">$1,500 - $5,000</option>
          <option value="$5,000+">$5,000+</option>
        </Input>
      </div>

      <Input
        label="Phone / WhatsApp (Optional)"
        name="phone"
        placeholder="+880 1700 000000 or WhatsApp"
        value={formData.phone}
        onChange={handleChange}
      />

      <Input
        as="textarea"
        label="Project Goals & Scope Brief"
        name="notes"
        rows="3"
        placeholder="Briefly describe what you want to build, key features, target timeline, or reference websites..."
        value={formData.notes}
        onChange={handleChange}
      />

      {errorMsg && (
        <div style={{ color: 'var(--danger-base)', fontSize: '13px', marginBottom: '14px' }} role="alert">
          {errorMsg}
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={loading}
        disabled={loading}
        style={{ width: '100%' }}
        rightIcon={<Send size={16} />}
      >
        {loading ? 'Submitting Brief...' : 'Request Consultation & Estimate'}
      </Button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginTop: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <ShieldCheck size={14} color="var(--success-base)" /> 100% Confidential
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Sparkles size={14} color="var(--accent-base)" /> Proposal within 24h
        </span>
      </div>
    </form>
  );
}

export default ConsultationForm;
